import { ModalArgs, ModalRef, ModalResult } from '@msdk/common'
import { ModalBase } from '@msdk/ui'
import React, { createContext, useContext, useMemo, useState } from 'react'

import { parseSize } from '../../utils'

interface ModalState {
  modals: ModalRef[]
  get: (id: string) => ModalRef | undefined
  add: (
    id: string,
    modalBody: React.ReactElement,
    dismiss: () => void,
    size?: string,
    args?: ModalArgs,
  ) => ModalRef | null
  remove: (id: string) => void
}

const INITIAL_STATE: ModalState = {
  modals: [],
  get: () => undefined,
  add: () => null,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  remove: () => {},
}

const modalContext = createContext<ModalState>(INITIAL_STATE)
export const useModalContext = () => useContext(modalContext)

export const ModalContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [modals, setModals] = useState<ModalRef[]>([])

  const get = (id: string) => modals.find((m) => m.id === id)

  const add = (
    id: string,
    modalBody: React.ReactElement,
    dismiss: (args?: ModalResult) => void | Promise<void>,
    size?: string,
    args?: ModalArgs,
  ) => {
    const onModalDismiss = (modalResult?: ModalResult) => {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      remove(id)
      dismiss(modalResult)
    }
    const modalRef = new ModalRef(id, onModalDismiss)

    const component = React.cloneElement(modalBody, {
      ...modalBody.props,
      modalRef,
    })

    const sizes = parseSize(size)

    modalRef.component = args?.renderDirect
      ? component
      : React.cloneElement(
          <ModalBase
            id={crypto.randomUUID()}
            key={modalRef.key}
            body={component}
            isOpen={true}
            onDismiss={onModalDismiss}
            width={sizes.width}
            height={sizes.height}
            isFullScreen={size?.toLowerCase() === 'fullscreen'}
            disableOutsidePointerEvents={args?.preventClickOutside ?? true}
            preventEscapeFromClosing={args?.preventEscapeFromClosing ?? true}
          />,
        )

    // eslint-disable-next-line @typescript-eslint/no-shadow
    setModals((modals) => [...modals, modalRef])
    return modalRef
  }

  const remove = (id: string) => {
    setModals((ms) => {
      const index = ms.findIndex((m) => m.id === id)
      if (index !== -1) {
        const newModals = [...ms]
        newModals.splice(index, 1)
        return newModals
      }
      return ms
    })
  }

  const context: ModalState = useMemo(() => {
    return { modals, get, add, remove }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modals])

  return (
    <modalContext.Provider value={context}>
      {children}
      {modals.map((m, index) => {
        return (
          <div key={m.key}>
            {m.status === 'Open' &&
              React.isValidElement(m.component) &&
              React.cloneElement(m.component, { stackIndex: index })}
          </div>
        )
      })}
    </modalContext.Provider>
  )
}
